import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Icon from '@amzn/meridian/icon'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import geopinTokens from '@amzn/meridian-tokens/base/icon/geopin'
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small'

import { getStoreId, getSelectedStoreId, getStoreList, isStoreListLoaded, getStoreScanStatus } from '@atredux/selectors/storeSelector'
import { getNotificationSheetState } from '@atredux/selectors/notificationSelector'
import { setStoreList, setStoreId, setSelectedStoreId } from '@atredux/slices/storeSlice'
import { RootState } from '@atredux/store'
import { getUserStores, StoreDetails } from '@api/userAndStoreContext'
import { mapStoresToStoreDetails } from '@pages/StoreSelectionPage/utils/storeMapper'
import { ARIA_CURRENT_STORE, CHOOSE_STORE_DEFAULT, SCAN_INTO_STORE } from '@pages/StoreSelectionPage/constants'
import { StoreSelectionSheet } from '@pages/StoreSelectionPage/StoreSelectionSheet'
import { IStoreSelectionResult } from '@pages/StoreSelectionPage/StoreSelectionPageContainer'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getAssociateEntryTool, getStoreScanPolicy } from '@services/ToolMenuConfigService'
import { getToolLaunchStoreId } from '@atredux/selectors/viewOnlySelector'
import { useViewOnlySelector } from '@hooks/useViewOnlySelector'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

import './StoreSwitcherHeader.scss'

interface IStoreSwitcherHeaderProps {
    // Optional caller-provided callback, run after a store is successfully switched.
    onStoreSwitched?: () => void
}

/**
 * StoreSwitcherHeader component displays the current store name with interactive
 * elements to allow users to switch stores. It shows a geopin icon, store name,
 * and chevron down icon, positioned directly below the NavBar.
 *
 * When clicked, it opens the StoreSelectionSheet to allow store switching.
 *
 * @returns {React.ReactElement}
 */
const StoreSwitcherHeader = ({ onStoreSwitched }: Readonly<IStoreSwitcherHeaderProps>): React.ReactElement => {
    const dispatch = useDispatch()
    const currentStoreId = useSelector(getStoreId)
    const selectedStoreId = useSelector(getSelectedStoreId)
    const selectedTool = useSelector(getSelectedTool)
    const storeList = useSelector(getStoreList)
    const storeListLoaded = useSelector(isStoreListLoaded)
    const isCurrentScanned = useSelector((state: RootState) => currentStoreId ? getStoreScanStatus(state, currentStoreId) : false)
    const isSelectedStoreScanned = useSelector((state: RootState) => selectedStoreId ? getStoreScanStatus(state, selectedStoreId) : false)
    const isNotificationsOpen = useSelector(getNotificationSheetState)
    // Only set for a tool in the 'viewOnly' store-scan tier. Memoized so useViewOnlySelector's
    // keyed selector only changes when the tool or the flag does.
    const viewOnlyLaunchStoreSelector = useCallback((state: RootState) =>
        getStoreScanPolicy(selectedTool) === 'viewOnly' ? getToolLaunchStoreId(state, selectedTool) : undefined,
    [selectedTool])
    const viewOnlyLaunchStoreId = useViewOnlySelector(viewOnlyLaunchStoreSelector)
    const isViewOnlyLaunchStoreScanned = useSelector((state: RootState) =>
        viewOnlyLaunchStoreId ? getStoreScanStatus(state, viewOnlyLaunchStoreId) : false)
    const [bundle] = useBundle(BUNDLE_NAME)

    const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
    // useBundle starts at `bundle: undefined` and resolves asynchronously, so t()
    // returns undefined on the first render or two. Real copy arrives via the effect
    // below, which lists `bundle` as a dependency — a useState initializer runs only
    // at mount and would otherwise freeze the unresolved value forever. The ''
    // coercion just keeps the declared string type honest during that gap.
    const chooseStoreDefault = () => t(bundle, CHOOSE_STORE_DEFAULT) || ''

    const [storeName, setStoreName] = useState<string>(chooseStoreDefault)
    const [isLoadingStores, setIsLoadingStores] = useState<boolean>(false)
    const [hasUserSelectedStore, setHasUserSelectedStore] = useState<boolean>(false)

    const headerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (selectedStoreId || currentStoreId) {
            setHasUserSelectedStore(true)
        }
    }, [selectedStoreId, currentStoreId])

    const determineDisplayedStore = () => {
        const isAssociateEntry = selectedTool?.displayName === 'Associate Entry'
        const hasNeverScanned = !currentStoreId || currentStoreId === 'NO_CURRENT_STORE'
        let displayStoreId: string | undefined
        let showScanMessage = false

        /**
         * User has never scanned into any store — show the selectedStoreId
         * with a scan message on all tools.
         */
        if (hasNeverScanned && selectedStoreId) {
            displayStoreId = selectedStoreId
            showScanMessage = true
        /**
         * Associate Entry shows the selectedStoreId with a scan message
         * if the user hasn't scanned into that store yet.
         */
        } else if (isAssociateEntry && selectedStoreId) {
            displayStoreId = selectedStoreId
            showScanMessage = !isSelectedStoreScanned
        /**
         * View-only tools (e.g. Planogram) open for the store getLaunchStoreId picks: the
         * selected store, else the scanned-into one. Show that store so the header matches
         * the store the tool is showing, with a scan message while it is not scanned into.
         */
        } else if (viewOnlyLaunchStoreId) {
            displayStoreId = viewOnlyLaunchStoreId
            showScanMessage = !isViewOnlyLaunchStoreScanned
        /**
         * All other tools show the currentStoreId (the store the user has scanned into).
         */
        } else if (currentStoreId && isCurrentScanned) {
            displayStoreId = currentStoreId
            showScanMessage = false
        }

        return { displayStoreId, showScanMessage }
    }

    const formatStoreName = (store: StoreDetails, showScanMessage: boolean) => {
        const name = store.storeName || store.storeId || ''
        return showScanMessage ? (t(bundle, SCAN_INTO_STORE, { storeName: name }) || name) : name
    }

    const findStoreInList = (stores: StoreDetails[], storeId: string) => {
        return stores.find((s: StoreDetails) => s.storeId === storeId)
    }

    const fetchAndSetStoreName = async (displayStoreId: string, showScanMessage: boolean) => {
        setIsLoadingStores(true)
        try {
            const stores = mapStoresToStoreDetails(await getUserStores(displayStoreId))
            dispatch(setStoreList([...stores].sort((a, b) => a.storeName.localeCompare(b.storeName))))

            const store = findStoreInList(stores, displayStoreId)
            setStoreName(store ? formatStoreName(store, showScanMessage) : chooseStoreDefault())
        } catch (error) {
            console.error('Failed to fetch store list:', error)
            setStoreName(chooseStoreDefault())
        } finally {
            setIsLoadingStores(false)
        }
    }

    useEffect(() => {
        const { displayStoreId, showScanMessage } = determineDisplayedStore()
        if (!hasUserSelectedStore || !displayStoreId) {
            setStoreName(chooseStoreDefault())
            return
        }

        if (storeListLoaded && storeList.length > 0) {
            const store = findStoreInList(storeList, displayStoreId)
            setStoreName(store ? formatStoreName(store, showScanMessage) : chooseStoreDefault())
        } else {
            fetchAndSetStoreName(displayStoreId, showScanMessage)
        }
        // `bundle` is a dependency because storeName is resolved copy, not an ID —
        // without it a locale change would leave the previous language's text (or the
        // empty initial value) in the header until some other dependency happened to
        // change. Re-running is cheap when the store list is already loaded.
    }, [currentStoreId, selectedStoreId, selectedTool, storeList, storeListLoaded, dispatch, hasUserSelectedStore, isCurrentScanned, isSelectedStoreScanned, isNotificationsOpen, bundle, viewOnlyLaunchStoreId, isViewOnlyLaunchStoreScanned])

    const handleHeaderClick = () => {
        setIsSheetOpen(true)
    }

    const handleSheetClose = () => {
        setIsSheetOpen(false)

        // Return focus to header when sheet closes
        if (headerRef.current) {
            headerRef.current.focus()
        }
    }

    const handleStoreSelectionComplete = (result?: IStoreSelectionResult) => {
        // Re-picking the store whose scan-in is already active changes nothing, so keep the
        // associate where they are (e.g. on Store Tools) instead of sending them to Associate Entry.
        if (result?.needsScanIn === false) {
            setIsSheetOpen(false)
            return
        }

        // Navigate to Associate Entry Tool
        const tool = getAssociateEntryTool()
        if (tool) {
            _navigateToTool(tool)
        }

        // Close the sheet after successful switch
        setIsSheetOpen(false)

        // Call the custom function provided by the caller (if any) now the switch succeeded.
        onStoreSwitched?.()
    }

    return (
        <>
            <div
                ref={headerRef}
                className="store-switcher-header"
                data-atb-id="store-switcher-header"
                onClick={handleHeaderClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleHeaderClick()
                    }
                }}
                aria-label={t(bundle, ARIA_CURRENT_STORE, { storeName })}
            >
                <Row
                    alignmentVertical="center"
                    alignmentHorizontal="justify"
                    width="100%"
                >
                    <Row alignmentVertical="center" spacing="300" maxWidth="100%" minWidth="0">
                        <Icon tokens={geopinTokens} className="header-icon" />
                        <Text className="store-name" data-atb-id="store-switcher-store-name" type="b300">
                            {storeName}
                        </Text>
                    </Row>
                    <Icon tokens={chevronDownSmallTokens} className="header-icon" />
                </Row>
            </div>

            <StoreSelectionSheet
                open={isSheetOpen}
                onClose={handleSheetClose}
                onComplete={handleStoreSelectionComplete}
                showSwitchButton={true}
            />
        </>
    )
}

export default StoreSwitcherHeader
